/**
 * KokoroTTSService — Main-thread TTS service backed by a Web Worker.
 *
 * This service is the primary interface for all TTS operations in Faana.
 * The model runs in a Web Worker (kokoro-tts.worker.js) so the UI thread
 * never blocks during ONNX model loading or audio synthesis.
 *
 * Plan 01 scope: init(), isReady(), isModelLoading(), _resolveVoicePreset()
 * Plan 02 adds: speak(), stop(), previewVoice(), audio playback, LRU cache
 *
 * Usage:
 *   const tts = new KokoroTTSService();
 *   await tts.init((progress) => console.log(progress));
 *   tts.speak('Hello world', 'af_heart', {
 *     onSpeakingStart: () => console.log('started'),
 *     onSpeakingEnd: () => console.log('finished'),
 *   });
 */

import { createKokoroWorker } from './createKokoroWorker';
import { chunkText } from './textChunker';

/**
 * Known Kokoro voice IDs.
 * Any voice name NOT in this set is treated as a legacy browser voice
 * and auto-migrated to DEFAULT_VOICE.
 *
 * Source: https://huggingface.co/onnx-community/Kokoro-82M-v1.0-ONNX
 * Verified voice IDs available in the model.
 */
const KOKORO_VOICE_IDS = new Set([
  // American female
  'af_heart', 'af_alloy', 'af_aoede', 'af_bella', 'af_jessica',
  'af_kore', 'af_nicole', 'af_nova', 'af_river', 'af_sarah', 'af_sky',
  // American male
  'am_adam', 'am_echo', 'am_eric', 'am_fenrir', 'am_liam',
  'am_michael', 'am_onyx', 'am_orion', 'am_puck',
  // British female
  'bf_alice', 'bf_emma', 'bf_isabella', 'bf_lily',
  // British male
  'bm_daniel', 'bm_fable', 'bm_george', 'bm_lewis',
]);

// ---------------------------------------------------------------------------
// Internal LRU cache (~20 lines)
// ---------------------------------------------------------------------------

/**
 * Map-based LRU cache with configurable max size.
 * Uses delete + re-insert pattern for O(1) recency updates.
 *
 * Cache key pattern: "${text}::${voiceId}"
 * Cache value: { audio: Float32Array, sampleRate: number }
 */
class LRUCache {
  constructor(maxSize = 50) {
    this._cache = new Map();
    this._maxSize = maxSize;
  }

  get(key) {
    if (!this._cache.has(key)) return undefined;
    // Refresh recency: delete and re-insert
    const value = this._cache.get(key);
    this._cache.delete(key);
    this._cache.set(key, value);
    return value;
  }

  set(key, value) {
    if (this._cache.has(key)) {
      // Re-insert for recency
      this._cache.delete(key);
    } else if (this._cache.size >= this._maxSize) {
      // Evict least recently used (first/oldest entry in Map iteration order)
      this._cache.delete(this._cache.keys().next().value);
    }
    this._cache.set(key, value);
  }

  has(key) {
    return this._cache.has(key);
  }
}

// ---------------------------------------------------------------------------
// KokoroTTSService
// ---------------------------------------------------------------------------

export class KokoroTTSService {
  /**
   * Default voice: American female, warm coaching tone.
   * Used when voice is unset or is a legacy browser voice name.
   */
  static DEFAULT_VOICE = 'af_heart';

  constructor() {
    // Worker reference — null until init() is called
    this._worker = null;

    // Model state flags
    this._ready = false;
    this._loading = false;

    // Singleton init promise — reused on subsequent init() calls
    this._initPromise = null;

    // Progress callback stored during init
    this._progressCallback = null;

    // Resolve/reject functions for the init promise
    this._initResolve = null;
    this._initReject = null;

    // --- Plan 02 state ---

    // Web Audio API context for audio playback — created lazily on first speak()
    this._audioContext = null;

    // Scheduled start time for next audio segment (gapless playback)
    this._nextStartTime = 0;

    // Active AudioBufferSourceNodes (for stop())
    this._activeSources = [];

    // Queue of pending synthesis chunks (chunk objects awaiting audio)
    this._chunkQueue = [];

    // Whether TTS is currently speaking
    this._isSpeaking = false;

    // LRU cache for synthesized audio
    // Key: "${text}::${voiceId}", value: { audio: Float32Array, sampleRate }
    this._cache = new LRUCache(50);

    // Callbacks for the current speak() call
    this._callbacks = {};

    // Map of synthesis request id -> { resolve, reject, text, voiceId }
    this._pendingRequests = new Map();

    // Count of pending chunks (chunks sent to worker but not yet scheduled)
    this._pendingChunkCount = 0;

    // Total chunks expected for the current speak() call
    this._totalChunkCount = 0;

    // Playback speed (applied via playbackRate.value in Web Audio API)
    this._speed = 1.0;

    // Playback volume (applied via GainNode.gain.value)
    this._volume = 1.0;

    // Whether onSpeakingStart has been called for the current speak()
    this._speakingStartFired = false;
  }

  // ---------------------------------------------------------------------------
  // Public API — Plan 01
  // ---------------------------------------------------------------------------

  /**
   * Initialize the TTS service by loading the Kokoro model in a Web Worker.
   *
   * Lazy initialization: the model is not downloaded until this is called.
   * Singleton: subsequent calls return the same promise — the model is only
   * loaded once per service instance.
   *
   * @param {function} [progressCallback] - Called with progress data as the
   *   model downloads. Signature: (data: { status, file, progress?, loaded?, total? }) => void
   * @returns {Promise<void>} Resolves when the model is ready to synthesize.
   */
  init(progressCallback) {
    if (this._initPromise) {
      // Already initializing or initialized — return the same promise (singleton)
      return this._initPromise;
    }

    this._loading = true;
    this._progressCallback = progressCallback || null;

    // Create the init promise with captured resolve/reject
    this._initPromise = new Promise((resolve, reject) => {
      this._initResolve = resolve;
      this._initReject = reject;
    });

    // Create worker and wire up message handler
    this._worker = createKokoroWorker();
    this._worker.onmessage = this._handleWorkerMessage.bind(this);

    // Tell the worker to start loading the model
    this._worker.postMessage({ type: 'init' });

    return this._initPromise;
  }

  /**
   * Whether the model is loaded and ready to synthesize audio.
   * @returns {boolean}
   */
  isReady() {
    return this._ready;
  }

  /**
   * Whether the model is currently downloading/initializing.
   * @returns {boolean}
   */
  isModelLoading() {
    return this._loading;
  }

  // ---------------------------------------------------------------------------
  // Public API — Plan 02
  // ---------------------------------------------------------------------------

  /**
   * Synthesize and speak text using the given voice.
   *
   * Pipeline:
   *   1. If not ready, fall back to native TTS immediately
   *   2. Create AudioContext lazily (PLAY-04)
   *   3. Stop current speech if already speaking
   *   4. Chunk text via chunkText()
   *   5. For each chunk: check LRU cache; if miss, send to worker
   *   6. Pre-buffer: chunk N+1 sent to worker while chunk N plays
   *
   * @param {string} text - Text to synthesize (may contain markdown)
   * @param {string} [voiceId] - Kokoro voice ID; defaults to DEFAULT_VOICE
   * @param {object} [callbacks] - { onSpeakingStart, onSpeakingEnd, onSpeakingError }
   */
  speak(text, voiceId, callbacks) {
    const resolvedVoice = this._resolveVoicePreset(voiceId);
    const cbs = callbacks || {};

    // If not ready, fall back to native TTS immediately
    if (!this._ready) {
      this._fallbackToNativeTTS(text, cbs);
      return;
    }

    // Ensure AudioContext exists and is running (PLAY-04)
    this._ensureAudioContext();

    // Stop current speech if already speaking (clean interruption)
    if (this._isSpeaking) {
      this.stop();
    }

    // Store callbacks for this speak() call
    this._callbacks = cbs;
    this._speakingStartFired = false;

    // Chunk the text
    const chunks = chunkText(text);
    if (!chunks || chunks.length === 0) {
      // Nothing to say
      if (cbs.onSpeakingEnd) cbs.onSpeakingEnd();
      return;
    }

    this._isSpeaking = true;
    this._totalChunkCount = chunks.length;
    this._pendingChunkCount = chunks.length;

    // Process each chunk
    for (let i = 0; i < chunks.length; i++) {
      const chunk = chunks[i];
      const cacheKey = `${chunk}::${resolvedVoice}`;

      if (this._cache.has(cacheKey)) {
        // Cache hit — schedule directly without worker round-trip
        const cached = this._cache.get(cacheKey);
        this._scheduleChunk(cached.audio, cached.sampleRate);
        this._pendingChunkCount--;
        if (this._pendingChunkCount === 0) {
          // All chunks already cached — no onended needed for this path
          // _scheduleChunk tracks sources; onended handles final callback
        }
      } else {
        // Cache miss — send to worker for synthesis
        const id = `${Date.now()}-${i}-${Math.random().toString(36).slice(2)}`;
        this._pendingRequests.set(id, {
          resolve: (audioData) => {
            // Cache the result
            this._cache.set(cacheKey, audioData);
            // Schedule playback
            this._scheduleChunk(audioData.audio, audioData.sampleRate);
            this._pendingRequests.delete(id);
            this._pendingChunkCount--;
          },
          reject: (_err) => {
            // Silent fallback — try speechSynthesis for this chunk
            this._pendingRequests.delete(id);
            this._pendingChunkCount--;
            this._fallbackToNativeTTS(chunk, cbs);
          },
          text: chunk,
          voiceId: resolvedVoice,
        });

        this._worker.postMessage({
          type: 'synthesize',
          id,
          text: chunk,
          voiceId: resolvedVoice,
        });
      }
    }
  }

  /**
   * Stop current speech playback immediately.
   *
   * Stops all active AudioBufferSourceNodes, posts abort to worker,
   * clears pending requests and chunk queue.
   */
  stop() {
    // Stop all active audio sources
    this._activeSources.forEach(src => {
      try {
        src.stop();
      } catch (_) {
        // Already stopped — ignore
      }
    });
    this._activeSources = [];
    this._nextStartTime = 0;

    // Abort pending worker synthesis
    if (this._worker) {
      this._worker.postMessage({ type: 'abort' });
    }

    // Clear pending state
    this._pendingRequests.clear();
    this._chunkQueue = [];
    this._pendingChunkCount = 0;
    this._isSpeaking = false;

    // Fire onSpeakingEnd if callbacks registered
    if (this._callbacks && this._callbacks.onSpeakingEnd) {
      this._callbacks.onSpeakingEnd();
    }
  }

  /**
   * Preview a voice by synthesizing a short sample.
   * Called by Phase 8 VoiceSettings for the "test voice" button.
   *
   * @param {string} voiceId - Kokoro voice ID to preview
   */
  previewVoice(voiceId) {
    const sampleText = 'Hello, how can I help you today?';
    return this.speak(sampleText, voiceId, {});
  }

  /**
   * Set playback speed (applied via Web Audio API playbackRate).
   * Does not affect synthesis — speed is applied at playback time.
   *
   * @param {number} speed - Playback rate multiplier (e.g. 1.0 = normal, 1.5 = 50% faster)
   */
  setSpeed(speed) {
    this._speed = speed;
  }

  /**
   * Set playback volume (applied via Web Audio API GainNode).
   * Does not affect synthesis — volume is applied at playback time.
   *
   * @param {number} volume - Volume level (0.0 to 1.0)
   */
  setVolume(volume) {
    this._volume = volume;
  }

  // ---------------------------------------------------------------------------
  // Internal — Worker message handling
  // ---------------------------------------------------------------------------

  /**
   * Route messages from the worker to the appropriate handler.
   * @param {MessageEvent} event
   */
  _handleWorkerMessage(event) {
    const { type, id, ...rest } = event.data;

    switch (type) {
      case 'progress':
        if (this._progressCallback) {
          this._progressCallback(rest);
        }
        break;

      case 'ready':
        this._ready = true;
        this._loading = false;
        if (this._initResolve) {
          this._initResolve();
        }
        break;

      case 'audio':
        // Resolve pending synthesis request
        if (this._pendingRequests.has(id)) {
          const { resolve } = this._pendingRequests.get(id);
          resolve({ audio: rest.audio, sampleRate: rest.sampleRate });
        }
        break;

      case 'error':
        if (id === null || id === undefined) {
          // Init error
          this._loading = false;
          this._ready = false;
          if (this._initReject) {
            this._initReject(new Error(rest.message));
          }
        } else {
          // Synthesis error — silent fallback per user decision
          if (this._pendingRequests.has(id)) {
            const { reject } = this._pendingRequests.get(id);
            reject(new Error(rest.message));
          }
        }
        break;

      default:
        // Unknown message type — ignore
        break;
    }
  }

  // ---------------------------------------------------------------------------
  // Internal — AudioContext lifecycle
  // ---------------------------------------------------------------------------

  /**
   * Ensure an AudioContext exists and is running.
   *
   * Creates AudioContext only on first call (PLAY-04 — lazy creation on user gesture).
   * Handles iOS Safari "suspended" and WebKit Bug 237878 "interrupted" states.
   */
  _ensureAudioContext() {
    if (!this._audioContext) {
      this._audioContext = new AudioContext();

      // Handle state changes — resume if interrupted (WebKit Bug 237878)
      this._audioContext.onstatechange = () => {
        if (this._audioContext.state === 'interrupted') {
          this._audioContext.resume();
        }
      };
    }

    // Resume if suspended (iOS Safari autoplay policy)
    const state = this._audioContext.state;
    if (state === 'suspended' || state === 'interrupted') {
      this._audioContext.resume();
    }
  }

  // ---------------------------------------------------------------------------
  // Internal — Audio scheduling (PLAY-01 gapless playback)
  // ---------------------------------------------------------------------------

  /**
   * Schedule an audio chunk for gapless playback via Web Audio API.
   *
   * Uses AudioBufferSourceNode.start(nextStartTime) to pre-schedule chunks
   * so they play back-to-back with no gaps between them.
   *
   * @param {Float32Array} audioFloat32 - Raw PCM audio data
   * @param {number} sampleRate - Audio sample rate (typically 24000 Hz)
   */
  _scheduleChunk(audioFloat32, sampleRate) {
    const ctx = this._audioContext;
    if (!ctx) return;

    // Create AudioBuffer from Float32Array
    const buffer = ctx.createBuffer(1, audioFloat32.length, sampleRate);
    buffer.getChannelData(0).set(audioFloat32);

    // Create source node
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.playbackRate.value = this._speed;

    // Create gain node for volume control
    const gain = ctx.createGain();
    gain.gain.value = this._volume;

    // Connect: source -> gain -> destination
    source.connect(gain);
    gain.connect(ctx.destination);

    // Gapless scheduling: start at nextStartTime or currentTime (whichever is later)
    const startTime = Math.max(ctx.currentTime, this._nextStartTime);
    source.start(startTime);

    // Advance nextStartTime by this chunk's duration (accounting for speed)
    this._nextStartTime = startTime + buffer.duration / this._speed;

    // Track for stop()
    this._activeSources.push(source);

    // Fire onSpeakingStart on first chunk
    if (!this._speakingStartFired) {
      this._speakingStartFired = true;
      if (this._callbacks && this._callbacks.onSpeakingStart) {
        this._callbacks.onSpeakingStart();
      }
    }

    // When chunk ends: remove from active sources, check if all done
    source.onended = () => {
      const idx = this._activeSources.indexOf(source);
      if (idx !== -1) {
        this._activeSources.splice(idx, 1);
      }

      // If no more active sources and no pending requests, all done
      if (this._activeSources.length === 0 && this._pendingRequests.size === 0) {
        this._isSpeaking = false;
        if (this._callbacks && this._callbacks.onSpeakingEnd) {
          this._callbacks.onSpeakingEnd();
        }
      }
    };
  }

  // ---------------------------------------------------------------------------
  // Internal — Native TTS fallback (PLAY-03)
  // ---------------------------------------------------------------------------

  /**
   * Fall back to browser's native speechSynthesis API.
   *
   * Per user decision: silent fallback — no toast or notification.
   * Wires utterance events to the provided callbacks.
   *
   * @param {string} text - Text to speak
   * @param {object} callbacks - { onSpeakingStart, onSpeakingEnd, onSpeakingError }
   */
  _fallbackToNativeTTS(text, callbacks) {
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = this._speed;
    utterance.volume = this._volume;

    if (callbacks) {
      utterance.onstart = callbacks.onSpeakingStart || null;
      utterance.onend = () => {
        this._isSpeaking = false;
        if (callbacks.onSpeakingEnd) callbacks.onSpeakingEnd();
      };
      utterance.onerror = callbacks.onSpeakingError || null;
    }

    window.speechSynthesis.speak(utterance);
  }

  // ---------------------------------------------------------------------------
  // Internal — Voice resolution
  // ---------------------------------------------------------------------------

  /**
   * Resolve a voice name to a valid Kokoro voice ID.
   *
   * If the name is a known Kokoro voice ID, return it unchanged.
   * Otherwise, treat it as a legacy browser voice name (e.g. "Google US English")
   * and return the default voice, auto-migrating the user to Kokoro.
   *
   * @param {string|null|undefined} voiceName
   * @returns {string} A valid Kokoro voice ID
   */
  _resolveVoicePreset(voiceName) {
    if (voiceName && KOKORO_VOICE_IDS.has(voiceName)) {
      return voiceName;
    }
    return KokoroTTSService.DEFAULT_VOICE;
  }
}
